//Hooks
import { get } from 'svelte/store';
//Formating
import { format } from 'svelte-i18n';
//Stores
import { devices_settings, devices_status } from 'src/stores/device-runtime/status';
//Helpers
import { real_id } from '../interface_helpers';
import { dateFormat } from '../date_helper';
import {
	bthomeObjectInfos,
	bthomeObjectTitle,
	getBthomeButtonStatus,
	getBthomeButtonValue,
} from './virtual_bthome_helper';
import { getComponentByDevice, getVirtualComponentsInterface } from './virtual_components_helpers';
import { getWindDirection } from '../../components/WeatherStation/weather_station_helpers';
import { getVirtualTemplate } from './template_helpers/virtual_template_helpers';
import { VIRTUAL_TEMPLATES } from './template_helpers/virtual_template_config';
import { convertToUnit, skipUnitConversion } from '../units_helpers';
import { getVirtualIconStatus } from './virtual_icons_helpers';
//Types
import type {
	VCInterfaceStatus,
	VirtualComponentConfig,
	VirtualComponentInterface,
	VirtualComponentInterfaceData,
	VirtualComponentTypes,
	VirtualComponentUI,
	VirtualIcon,
} from 'src/types/VirtualComponents';
import type { Device } from '../../types/Device';
import type { DeviceStatusG2 } from 'src/types/DeviceStatusG2';
import type { VirtualDeviceTemplate } from './template_helpers/virtual_template_types';
import type { DeviceSettingsG2 } from 'src/types/DeviceSettings';

export const DECIMAL_PRECISION_MIN = 1;
export const DECIMAL_PRECISION_MAX = 5;

const getVirtualDecimalPrecision = (device: Device): number => {
	const parsedValue = Number(device?.virtualDecimalPrecision);
	if (!Number.isInteger(parsedValue)) {
		return DECIMAL_PRECISION_MIN;
	}
	return Math.min(DECIMAL_PRECISION_MAX, Math.max(DECIMAL_PRECISION_MIN, parsedValue));
};

/**
 * Builds an array of VirtualComponentInterfaceStatus objects
 * @param {Device} device - The device object
 * @param {string[]} virtualComponents - An array of VirtualComponentInterfaceStatus objects
 * @param {number} groupID - The groupID to use to filter the VirtualComponentInterfaceStatus objects
 * @param {boolean} showHiddenComponent - Whether or not to show hidden components
 * @return {VCInterfaceStatus[]} - An array of VirtualComponentInterfaceStatus objects
 */
export const getVComponentStatus = (
	device: Device,
	virtualComponents: string[] = null,
	groupID: number = NaN,
	showHiddenComponent = false,
	virtualTemplate: VirtualDeviceTemplate = null,
): VCInterfaceStatus[] => {
	const id = real_id(device?.id);
	const deviceStatus = devices_status.getByRealId(device?.id);
	const deviceSettings = devices_settings.getByRealId(device?.id);
	const virtualDecimalPrecision = getVirtualDecimalPrecision(device);
	let interface_data: VCInterfaceStatus[] = [];
	if (!device || !deviceStatus || !deviceSettings) {
		return interface_data;
	}
	let vComponents = virtualComponents
		? virtualComponents
		: deviceStatus?.[`group:${groupID || device.channel}`]?.value;
	const templateDevice = virtualTemplate || getVirtualTemplate(device);
	if (templateDevice) {
		vComponents = Object.values(templateDevice);
	} else if (!virtualComponents && isNaN(groupID) && /^(bthome-device)$/.test(device.type)) {
		const group = getComponentByDevice(device);
		const groupAddr = deviceSettings[group]?.addr;
		vComponents = getVirtualComponentsInterface(id, 'bthomedevice|bthomesensor')
			.filter((i) => i.config?.addr == groupAddr)
			.map((i) => i.key);
	}
	if (vComponents?.length) {
		interface_data = buildVComponentStatus(
			deviceStatus,
			deviceSettings,
			vComponents,
			showHiddenComponent,
			templateDevice,
			virtualDecimalPrecision,
			interface_data,
		);
	}
	return interface_data;
};

/**
 * Returns a VirtualComponentInterfaceData object with the value, unit, icon, and label
 * of the given VirtualComponentInterfaceStatus object.
 * @param {DeviceStatusG2} deviceStatus - The device status object
 * @param {string} component - The component string
 * @param {VirtualComponentTypes} type - The type of the VirtualComponentInterfaceStatus object
 * @param {VirtualComponentConfig} config - The VirtualComponentConfig object
 * @param {boolean} showHiddenComponent - Whether or not to show hidden components
 * @returns {VirtualComponentInterfaceData} - The VirtualComponentInterfaceData object
 */
export const getVComponentValue = (
	deviceStatus: DeviceStatusG2,
	component: string,
	type: VirtualComponentTypes,
	config: VirtualComponentConfig,
	showHiddenComponent = false,
): VirtualComponentInterfaceData => {
	const status: VirtualComponentInterface['status'] = deviceStatus?.[component];
	const configUI: VirtualComponentUI = config?.['ui'] || config?.meta?.ui || {};
	const view = configUI?.view;
	let result: VirtualComponentInterfaceData = {
		event: null,
		value: null,
		unit: '',
		icon: '',
		label: '',
		isBleButton: config?.obj_id == 58,
	};
	if (!status || !config || (!showHiddenComponent && view === '' && !/^bthome/.test(component))) {
		return result;
	}
	if (type == 'button' || result.isBleButton) {
		result.event = deviceStatus?.[`v_eve${result.isBleButton ? '_bth' : ''}:${config.id}`]?.ev;
		if (result.isBleButton && !result.event) {
			// Since FW 1.6.0-beta2, BTHomeSensor (button) components remain in the components list
			// and await events from the bthomedevice component instead of bthomesensor.
			// https://gitlab.allterco.net/Shelly/frontend/shelly-app-3/-/issues/2607
			const deviceSettings = devices_settings.getByRealId(deviceStatus?.id);
			const bthomeDevice = Object.keys(deviceSettings || {}).find(
				(i) => /^bthomedevice:/.test(i) && deviceSettings?.[i]?.addr == config?.addr,
			);
			if (deviceStatus?.[bthomeDevice]?.id) {
				result.event = getBthomeButtonValue(
					deviceStatus,
					{ id: deviceStatus[bthomeDevice].id },
					config.idx,
				).event;
			}
		}
		return result;
	}
	result.value = status.value;
	if (
		!type.startsWith('bthome') &&
		(result.value == null || (/^(enum|text)$/.test(type) && !result.value))
	) {
		result.value = config.default_value;
	}
	if (type == 'bthomedevice') {
		let viewValue = status[view];
		if (view == 'battery') {
			result.unit = '%';
		} else if (view == 'rssi') {
			result.unit = 'dBm';
		} else if (status.last_updated_ts > 0) {
			viewValue = dateFormat(status.last_updated_ts * 1000, 'd/m/y H:i:s D');
		}
		result.value = viewValue;
	} else if (type == 'boolean') {
		result.event = status.value;
		if (configUI.titles) {
			result.label = configUI?.titles?.[result.value ? 1 : 0];
		}
	} else if (type == 'enum') {
		result.event = status.value;
		if (configUI.titles) {
			result.label = configUI?.titles?.[result.value] || config?.options?.[result.value];
		}
	} else if (type == 'number') {
		result.min = config.min ?? 0;
		result.max = config.max || 100;
		result.step = config.step || 1;
	}
	const icon: VirtualIcon = getVirtualIconStatus(
		result.event !== null ? result.event : result.value,
		type,
		config,
	);
	result = {
		...result,
		icon: icon?.icon || '',
		label: result.label ? result.label : icon?.label || '',
		iconColor: icon?.color,
	};
	if (result.value == null || (view == 'Last updated' && status.last_updated_ts <= 0)) {
		if (type.startsWith('bthome') && config?.obj_id !== 58) {
			result.value = get(format)('waiting_for_data', {
				default: 'Waiting for data',
			});
			return result;
		}
		result.value = 'N/A';
	}
	if (type == 'bthomesensor') {
		const bthomeObj = bthomeObjectInfos.find((i) => i.id == config?.obj_id);
		result.unit = bthomeObj?.unit;
		//Wind direction
		if (config?.obj_id == 94) {
			const direction = getWindDirection(result.value);
			result.value = `${result.value} ${result.unit} ${direction.title}`;
			result.icon = direction.icon;
			result.unit = '';
		}
	} else if (!result.unit) {
		result.unit = configUI?.unit || config?.unit || '';
	}
	return result;
};

export const getParentVirtualDeviceID = (id: string): string => {
	return id?.replace ? id.replace(/(_2\d{2})$/, '') : id;
};

/**
 * Builds an array of VirtualComponentInterfaceStatus objects given a deviceStatus and deviceSettings map.
 * @param {Object} deviceStatus - A map of device status where key is the component id and value is the component status.
 * @param {Object} deviceSettings - A map of device settings where key is the component id and value is the component settings.
 * @param {string[]} vComponents - An array of virtual component ids.
 * @param {boolean} showHiddenComponent - A boolean to show or hide the component with value of null.
 * @param {VirtualDeviceTemplate} templateDevice - The virtual device template object.
 * @param {number} virtualDecimalPrecision - The decimal precision for virtual components.
 * @param {VCInterfaceStatus[]} interface_data - An array of VirtualComponentInterfaceStatus objects to be populated.
 * @returns {VCInterfaceStatus[]} An array of VirtualComponentInterfaceStatus objects.
 */
const buildVComponentStatus = (
	deviceStatus: DeviceStatusG2,
	deviceSettings: DeviceSettingsG2,
	vComponents: string[],
	showHiddenComponent: boolean,
	templateDevice: VirtualDeviceTemplate,
	virtualDecimalPrecision: number,
	interface_data: VCInterfaceStatus[],
): VCInterfaceStatus[] => {
	for (const vc in vComponents) {
		const component: string = vComponents[vc];
		const status: VirtualComponentInterface['status'] = deviceStatus[component];
		const config: VirtualComponentConfig = deviceSettings[component];
		if (!status || !config) {
			continue;
		}
		const configUI: VirtualComponentUI = config?.['ui'] || config?.meta?.ui || {};
		const type: VirtualComponentTypes = component?.split(':')?.[0] as VirtualComponentTypes;
		const valueData = getVComponentValue(deviceStatus, component, type, config, showHiddenComponent);
		const validData =
			type == 'button' || valueData.isBleButton || type == null || valueData?.value !== null;
		if (!validData) {
			continue;
		}
		let role = undefined;
		let advLabel = false;
		let vcName = config.name;
		const vcType = valueData.isBleButton ? 'button' : type;
		const vcView = config?.['ui']?.view || config?.meta?.ui?.view;
		const hasButton =
			!valueData.isBleButton &&
			(vcType == 'button' || (vcType == 'boolean' && /^(toggle|button)$/.test(vcView)));
		const bthomeName = bthomeObjectInfos.find((i) => i.id == config?.obj_id)?.name;
		if (templateDevice) {
			role = Object.keys(templateDevice).find((k) => templateDevice[k] === component);
			const templateComponent = VIRTUAL_TEMPLATES[templateDevice.type]?.[role];
			valueData.icon =
				valueData.icon || templateComponent?.icons?.[valueData.event] || templateComponent?.icon;
			valueData.unit = valueData.unit || templateComponent?.unit || '';
			valueData.templateType = templateDevice.type;
			advLabel = vcType === 'boolean';
			vcName = templateComponent?.label
				? templateComponent.label({ translate: (k, d) => get(format)(k, { default: d }) })
				: config.name;
		}
		interface_data.push({
			...valueData,
			// The 'name' property is used exclusively for Device Card customization.
			name: component,
			id: config.id,
			vcType,
			vcView,
			vcName: vcName || bthomeObjectTitle(config) || component,
			hasButton,
			value:
				valueData.label ||
				(vcType == 'button'
					? null
					: convertToUnit(valueData.value, valueData.unit, {
							fixed: virtualDecimalPrecision,
							dontConvert: skipUnitConversion(bthomeName),
						})),
			event: valueData.event !== null ? valueData.event : valueData.value,
			rawValue: valueData.value,
			wideBtnLabel:
				type == 'boolean' && configUI?.titles
					? configUI.titles[valueData.event ? 0 : 1]
					: valueData.label || null,
			role,
			component,
			advLabel,
			hideLabel: vcType == 'button',
			mainLabel: false,
		});
		if (/^(bthomedevice|bthomebutton):/.test(component)) {
			getBthomeButtonStatus(deviceStatus, config, component, interface_data);
		}
	}
	if (interface_data.find((i) => i?.vcType == 'button') == null) {
		const mainLabelStatus = interface_data.find((i) => !i.hasButton);
		if (mainLabelStatus) mainLabelStatus.mainLabel = true;
	}
	return interface_data;
};
